// A20 W7a — the rrweb replay surface.
//
// rrweb-player 2.1.1, pinned to the same minor as the `rrweb` the recorder uses
// (components/reporter/useRecorder.ts). That pairing is not cosmetic: the event
// schema moved between rrweb 1.x and 2.x, and a player from the wrong major
// renders a blank stage rather than erroring, which would be the worst possible
// failure for a triage tool.
//
// The player is loaded with a dynamic import so ~200 KB of replay engine stays
// out of the console's main bundle — every page in this app is eagerly imported,
// and only this one needs it.
//
// The stage is rendered inside a plain <div> that the Svelte component owns.
// React never touches those children, so there is no reconciliation conflict;
// the effect's cleanup destroys the instance.

import * as React from 'react';
import 'rrweb-player/dist/style.css';

/**
 * The slice of rrweb-player's surface this component uses.
 *
 * Declared locally rather than imported: the shipped .d.ts re-exports types
 * from `svelte`, which is NOT a dependency of the package, so its type graph is
 * only resolvable with skipLibCheck papering over it. A four-line local
 * contract is more honest than an `any` that pretends to be typed.
 */
interface PlayerProps {
  events: unknown[];
  width?: number;
  height?: number;
  autoPlay?: boolean;
  showController?: boolean;
  mouseTail?: boolean;
  speedOption?: number[];
}
interface PlayerInstance {
  $destroy?: () => void;
  triggerResize?: () => void;
}
type PlayerCtor = new (options: { target: HTMLElement; props: PlayerProps }) => PlayerInstance;

export interface ReplayPlayerProps {
  events: unknown[];
  /** Rendered above the stage — the mask banner belongs here, not in a tooltip. */
  banner?: React.ReactNode;
  height?: number;
}

export function ReplayPlayer({ events, banner, height = 460 }: ReplayPlayerProps) {
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const [mountError, setMountError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const host = hostRef.current;
    // rrweb needs a FullSnapshot plus at least one incremental event to have a
    // timeline. Below that it is not a short replay, it is not a replay.
    if (!host || events.length < 2) return undefined;

    let instance: PlayerInstance | null = null;
    let cancelled = false;

    (async () => {
      try {
        const mod = await import('rrweb-player');
        if (cancelled || !hostRef.current) return;
        const Ctor = (mod as { default: unknown }).default as PlayerCtor;
        hostRef.current.innerHTML = '';
        instance = new Ctor({
          target: hostRef.current,
          props: {
            events,
            width: hostRef.current.clientWidth || 900,
            height,
            // Never autoplay: a reviewer opening a ticket has not yet read the
            // banner telling them whether this is real customer text.
            autoPlay: false,
            showController: true,
            mouseTail: false,
            speedOption: [1, 2, 4, 8],
          },
        });
      } catch (err) {
        if (!cancelled) {
          setMountError(err instanceof Error ? err.message : 'The replay player could not be loaded.');
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        instance?.$destroy?.();
      } catch {
        /* a player that will not tear down cleanly must not break the page */
      }
      if (host) host.innerHTML = '';
    };
  }, [events, height]);

  if (events.length < 2) {
    return (
      <div style={{ padding: '28px 20px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
        The stored recording holds {events.length} event{events.length === 1 ? '' : 's'} — too few to replay.
        The report itself is unaffected.
      </div>
    );
  }

  return (
    <div>
      {banner && <div style={{ marginBottom: 12 }}>{banner}</div>}
      {mountError && (
        <div
          role="alert"
          style={{
            marginBottom: 12, padding: '10px 14px', borderRadius: 'var(--radius-md)',
            background: '#FEE2E2', border: '1px solid #FECACA', color: '#7F1D1D', fontSize: 13,
          }}
        >
          {mountError}
        </div>
      )}
      {/* Owned by the player; React deliberately renders no children into it. */}
      <div ref={hostRef} style={{ minHeight: height, background: 'var(--bg-muted)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }} />
    </div>
  );
}

export default ReplayPlayer;
